/**
 * ResetDekModal - Reset master key (DEK) with instructions and form.
 * Asks for: account password, old DEK (3 parts), new DEK (3 parts), confirm new DEK (3 parts).
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { DekInput, validateDek } from './DekInput';
import './ResetDekModal.css';

const RESET_INSTRUCTIONS = [
  'マスターキーをリセットした後は、次の手順を実行してください。',
  '',
  '1. 管理者は必ず1回ログアウトし、再度ログインしてください。',
  '2. 本社従業員は、各自の現在のパスワードで1回ログインしてください。パスワードの変更は不要です。',
  '3. 上記のログインが完了すると、給与データの閲覧が可能になります。'
];

interface ResetDekModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    accountPassword: string;
    oldDekPart1: string;
    oldDekPart2: string;
    oldDekPart3: string;
    newDekPart1: string;
    newDekPart2: string;
    newDekPart3: string;
  }) => void;
  submitting?: boolean;
  /** API error message (e.g. invalid password or wrong old DEK) */
  apiError?: string;
}

export const ResetDekModal: React.FC<ResetDekModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
  apiError
}) => {
  const [accountPassword, setAccountPassword] = useState('');
  const [oldPart1, setOldPart1] = useState('');
  const [oldPart2, setOldPart2] = useState('');
  const [oldPart3, setOldPart3] = useState('');
  const [newPart1, setNewPart1] = useState('');
  const [newPart2, setNewPart2] = useState('');
  const [newPart3, setNewPart3] = useState('');
  const [confirmPart1, setConfirmPart1] = useState('');
  const [confirmPart2, setConfirmPart2] = useState('');
  const [confirmPart3, setConfirmPart3] = useState('');
  const [error, setError] = useState('');
  const displayError = apiError || error;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!accountPassword.trim()) {
      setError('アカウントパスワードを入力してください。');
      return;
    }
    const oldDek = oldPart1 + oldPart2 + oldPart3;
    const newDek = newPart1 + newPart2 + newPart3;
    const confirmDek = confirmPart1 + confirmPart2 + confirmPart3;
    const oldErr = validateDek(oldDek);
    if (oldErr) {
      setError('現在のマスターキー: ' + oldErr);
      return;
    }
    const newErr = validateDek(newDek);
    if (newErr) {
      setError('新しいマスターキー: ' + newErr);
      return;
    }
    if (newDek !== confirmDek) {
      setError('新しいマスターキー（確認）が一致しません。');
      return;
    }
    onSubmit({
      accountPassword: accountPassword.trim(),
      oldDekPart1: oldPart1,
      oldDekPart2: oldPart2,
      oldDekPart3: oldPart3,
      newDekPart1: newPart1,
      newDekPart2: newPart2,
      newDekPart3: newPart3
    });
  };

  const handleClose = () => {
    setAccountPassword('');
    setOldPart1('');
    setOldPart2('');
    setOldPart3('');
    setNewPart1('');
    setNewPart2('');
    setNewPart3('');
    setConfirmPart1('');
    setConfirmPart2('');
    setConfirmPart3('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="reset-dek-overlay"
      onClick={(e) => e.target === e.currentTarget && !submitting && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-dek-title"
    >
      <div className="reset-dek-box">
        <h2 id="reset-dek-title" className="reset-dek-title">
          マスターキー（DEK）のリセット
        </h2>

        <div className="reset-dek-instructions">
          <h3 className="reset-dek-instructions-heading">リセット後の手順</h3>
          <ul className="reset-dek-instructions-list">
            {RESET_INSTRUCTIONS.map((line, i) =>
              line === '' ? (
                <li key={i} className="reset-dek-instructions-spacer" aria-hidden="true" />
              ) : (
                <li key={i}>{line}</li>
              )
            )}
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="reset-dek-form">
          <div className="reset-dek-field">
            <label htmlFor="reset-dek-account-pwd" className="reset-dek-label">
              現在のアカウントパスワード
            </label>
            <input
              id="reset-dek-account-pwd"
              type="password"
              className="reset-dek-text-input"
              value={accountPassword}
              onChange={(e) => setAccountPassword(e.target.value)}
              disabled={submitting}
              autoComplete="current-password"
              placeholder="パスワードを入力"
            />
          </div>

          <div className="reset-dek-field">
            <label className="reset-dek-label">現在のマスターキー</label>
            <DekInput
              part1={oldPart1}
              part2={oldPart2}
              part3={oldPart3}
              onPart1Change={setOldPart1}
              onPart2Change={setOldPart2}
              onPart3Change={setOldPart3}
              disabled={submitting}
              showHint={false}
              aria-label="現在のマスターキー"
            />
          </div>

          <div className="reset-dek-field">
            <label className="reset-dek-label">新しいマスターキー</label>
            <DekInput
              part1={newPart1}
              part2={newPart2}
              part3={newPart3}
              onPart1Change={setNewPart1}
              onPart2Change={setNewPart2}
              onPart3Change={setNewPart3}
              disabled={submitting}
              showHint={false}
              aria-label="新しいマスターキー"
            />
          </div>

          <div className="reset-dek-field">
            <label className="reset-dek-label">新しいマスターキー（確認）</label>
            <DekInput
              part1={confirmPart1}
              part2={confirmPart2}
              part3={confirmPart3}
              onPart1Change={setConfirmPart1}
              onPart2Change={setConfirmPart2}
              onPart3Change={setConfirmPart3}
              disabled={submitting}
              showHint={false}
              aria-label="新しいマスターキー（確認）"
            />
          </div>

          {displayError && <p className="reset-dek-error" role="alert">{displayError}</p>}

          <div className="reset-dek-actions">
            <button
              type="button"
              className="reset-dek-btn reset-dek-btn-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="reset-dek-btn reset-dek-btn-submit"
              disabled={submitting}
            >
              {submitting ? '処理中...' : 'マスターキーをリセット'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
